import { FreightTabDto, FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import {
	FreightBookingDetailState,
	getFreightBookingDetail,
	validateFreightDetail,
} from '../../BookingWizardData/FreightBookingWizardData';
import { BookingAddOn } from '../../BookingWizardData/BookingAddOn';

const BulkFreightDetailsFormActions = {
	AddNewDetail: 'add_new_detail',
	EditDetail: 'edit_detail',
	DeleteDetail: 'delete_detail',
	UpdateDetailFreightCategory: 'update_detail_freight_category',
	UpdateDetailFreightType: 'update_detail_freight_type',
	UpdateDetailFreightHandlings: 'update_detail_freight_handlings',
	UpdateDetailDescription: 'update_detail_description',
	UpdateDetailQuantity: 'update_detail_quantity',
	UpdateDetailWeight: 'update_detail_weight',
	UpdateDetailLength: 'update_detail_length',
	UpdateDetailWidth: 'update_detail_width',
	UpdateDetailHeight: 'update_detail_height',
	UpdateDetailIncrementAddOn: 'update_detail_increment_add_on',
	UpdateDetailDecrementAddOn: 'update_detail_decrement_add_on',
	ValidateDetail: 'validate_detail',
	ValidateAndTryCollapse: 'validate_and_try_collapse_detail',
} as const;

export type BulkFreightDetailsFormAction =
	| { type: typeof BulkFreightDetailsFormActions.AddNewDetail }
	| { type: typeof BulkFreightDetailsFormActions.EditDetail; id: string }
	| { type: typeof BulkFreightDetailsFormActions.DeleteDetail; id: string }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailFreightCategory; id: string; freightCategoryId: string }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailFreightType; id: string; freightTypeId: string }
	| {
			type: typeof BulkFreightDetailsFormActions.UpdateDetailFreightHandlings;
			id: string;
			handlingIds: string[];
	  }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailDescription; id: string; description: string }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailQuantity; id: string; quantity: number }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailWeight; id: string; weight: number | null }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailLength; id: string; length: number | null }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailWidth; id: string; width: number | null }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailHeight; id: string; height: number | null }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailIncrementAddOn; id: string; addOnId: string }
	| { type: typeof BulkFreightDetailsFormActions.UpdateDetailDecrementAddOn; id: string; addOnId: string }
	| { type: typeof BulkFreightDetailsFormActions.ValidateDetail; id: string }
	| { type: typeof BulkFreightDetailsFormActions.ValidateAndTryCollapse; id: string };

export const BulkFreightDetailsFormActionCreators = {
	addNewDetail: () => ({ type: BulkFreightDetailsFormActions.AddNewDetail }),
	editDetail: (id: string) => ({ type: BulkFreightDetailsFormActions.EditDetail, id }),
	deleteDetail: (id: string) => ({ type: BulkFreightDetailsFormActions.DeleteDetail, id }),
	updateDetailFreightCategory: (id: string, freightCategoryId: string) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailFreightCategory,
		id,
		freightCategoryId,
	}),
	updateDetailFreightType: (id: string, freightTypeId: string) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailFreightType,
		id,
		freightTypeId,
	}),
	updateDetailFreightHandlings: (id: string, handlingIds: string[]) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailFreightHandlings,
		id,
		handlingIds,
	}),
	updateDetailDescription: (id: string, description: string) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailDescription,
		id,
		description,
	}),
	updateDetailQuantity: (id: string, quantity: number) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailQuantity,
		id,
		quantity,
	}),
	updateDetailWeight: (id: string, weight: number | null) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailWeight,
		id,
		weight,
	}),
	updateDetailLength: (id: string, length: number | null) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailLength,
		id,
		length,
	}),
	updateDetailWidth: (id: string, width: number | null) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailWidth,
		id,
		width,
	}),
	updateDetailHeight: (id: string, height: number | null) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailHeight,
		id,
		height,
	}),
	updateDetailIncrementAddOn: (id: string, addOnId: string) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailIncrementAddOn,
		id,
		addOnId,
	}),
	updateDetailDecrementAddOn: (id: string, addOnId: string) => ({
		type: BulkFreightDetailsFormActions.UpdateDetailDecrementAddOn,
		id,
		addOnId,
	}),
	validateFreightDetail: (id: string) => ({ type: BulkFreightDetailsFormActions.ValidateDetail, id }),
	validateAndTryCollapse: (id: string) => ({ type: BulkFreightDetailsFormActions.ValidateAndTryCollapse, id }),
};

export interface BulkFreightDetailsFormState {
	// Intialised states
	freightElements: FreightTabDto;
	freightTypeDtos: FreightTypeDto[];
	freightDimensionsHiddenOnStaticPriceEnabled: boolean;

	// Actively changing states
	expandedId: string;
	items: FreightBookingDetailState[];
}

export function bulkFreightDetailsFormReducer(
	state: BulkFreightDetailsFormState,
	action: BulkFreightDetailsFormAction,
): BulkFreightDetailsFormState {
	switch (action.type) {
		case BulkFreightDetailsFormActions.AddNewDetail: {
			const newItem = getFreightBookingDetail();

			// Validate previously expanded item
			const items = validateItemInList(state.items, state.expandedId, {
				freightTypeDtos: state.freightTypeDtos,
				freightDimensionsHiddenOnStaticPriceEnabled: state.freightDimensionsHiddenOnStaticPriceEnabled,
			});
			return {
				...state,
				items: [...items, newItem],
				expandedId: newItem.id,
			};
		}
		case BulkFreightDetailsFormActions.EditDetail: {
			// Validate previously expanded item
			const items = validateItemInList(state.items, state.expandedId, {
				freightTypeDtos: state.freightTypeDtos,
				freightDimensionsHiddenOnStaticPriceEnabled: state.freightDimensionsHiddenOnStaticPriceEnabled,
			});
			return { ...state, expandedId: action.id, items };
		}
		case BulkFreightDetailsFormActions.DeleteDetail: {
			const { id } = action;
			if (state.items.length < 2) {
				return state;
			}
			const items = state.items.filter(item => item.id !== id);
			const expandedId = state.expandedId === id ? items[items.length - 1].id : state.expandedId;
			return { ...state, items, expandedId };
		}
		case BulkFreightDetailsFormActions.UpdateDetailFreightCategory: {
			const { id, freightCategoryId } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.freightCategoryId;
				return { ...item, freightCategoryId, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailFreightType: {
			const { id, freightTypeId } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.freightTypeId;
				return { ...item, freightTypeId, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailFreightHandlings: {
			const { id, handlingIds } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.handlingIds;
				return { ...item, handlingIds, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailDescription: {
			const { id, description } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.description;
				return { ...item, description, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailQuantity: {
			const { id, quantity } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.quantity;
				return { ...item, quantity, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailWeight: {
			const { id, weight } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.weight;
				return { ...item, weight, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailLength: {
			const { id, length } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.length;
				return { ...item, length, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailWidth: {
			const { id, width } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.width;
				return { ...item, width, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailHeight: {
			const { id, height } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}
				const errors = { ...item.errors };
				delete errors.height;
				return { ...item, height, errors };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailIncrementAddOn: {
			const { id, addOnId } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}

				const index = item.addOns.findIndex(addOn => addOn.optionId === addOnId);

				// create new or increment existing
				const addOns =
					index === -1
						? [...item.addOns, new BookingAddOn({ optionId: addOnId, amount: 1 })]
						: item.addOns.map(addOn => {
								if (addOn.optionId !== addOnId) {
									return addOn;
								}
								return new BookingAddOn({ ...addOn, amount: addOn.amount + 1 });
							});

				return { ...item, addOns };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.UpdateDetailDecrementAddOn: {
			const { id, addOnId } = action;
			const items = state.items.map(item => {
				if (item.id !== id) {
					return item;
				}

				const index = item.addOns.findIndex(addOn => addOn.optionId === addOnId);

				// not found
				if (index === -1) {
					return item;
				}

				// decrements to zero, so remove from list
				if (item.addOns[index].amount === 1) {
					return { ...item, addOns: item.addOns.filter(addOn => addOn.optionId !== addOnId) };
				}

				// decrement
				const addOns = item.addOns.map(addOn => {
					if (addOn.optionId !== addOnId) {
						return addOn;
					}
					return new BookingAddOn({ ...addOn, amount: addOn.amount - 1 });
				});

				return { ...item, addOns };
			});
			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.ValidateDetail: {
			const { id } = action;
			const items = validateItemInList(state.items, id, {
				freightTypeDtos: state.freightTypeDtos,
				freightDimensionsHiddenOnStaticPriceEnabled: state.freightDimensionsHiddenOnStaticPriceEnabled,
			});

			if (state.items === items) {
				return state;
			}

			return { ...state, items };
		}
		case BulkFreightDetailsFormActions.ValidateAndTryCollapse: {
			const { id } = action;

			// Validate items
			const items = validateItemInList(state.items, id, {
				freightTypeDtos: state.freightTypeDtos,
				freightDimensionsHiddenOnStaticPriceEnabled: state.freightDimensionsHiddenOnStaticPriceEnabled,
			});
			const item = items.find(i => i.id === id);
			if (!item) {
				return state;
			}

			// Only collapse when no errors
			const canCollapse = Object.entries(item.errors).length === 0;

			return {
				...state,
				items,
				expandedId: canCollapse
					? '' // empty string will make all forms collapse
					: state.expandedId,
			};
		}
		default:
			console.warn('Unhandled action:', action);
			return state;
	}
}

export function initialBulkFreightDetailsFormState(
	freightDimensionsHiddenOnStaticPriceEnabled: boolean,
	freightElements: FreightTabDto,
	items: FreightBookingDetailState[],
): BulkFreightDetailsFormState {
	let expandedId = '';

	const newItems = [...items];

	if (newItems.length === 0) {
		const newItem = getFreightBookingDetail();
		newItems.push(newItem);
		expandedId = newItem.id;
	} else {
		expandedId = newItems[newItems.length - 1].id;
	}

	return {
		freightElements,
		freightTypeDtos: Object.values(freightElements.freightTypes).flat(),
		freightDimensionsHiddenOnStaticPriceEnabled,
		items: newItems,
		expandedId,
	};
}

/**
 * Evaluate errors to specifed item in list.
 */
function validateItemInList(
	items: FreightBookingDetailState[],
	id: string,
	context: {
		freightTypeDtos: FreightTypeDto[];
		freightDimensionsHiddenOnStaticPriceEnabled: boolean;
	},
): FreightBookingDetailState[] {
	const match = items.find(item => item.id === id);
	if (match) {
		const errors = validateFreightDetail(match, context);
		return items.map(item => {
			if (item.id !== id) {
				return item;
			}
			return { ...item, errors };
		});
	}
	return items;
}
